/**
 * 承認者の全項目修正(2026-08-26 会議で支払い済みのみ → 2026-09-17 発注者指示でどの段階でも・完了後も)。
 *
 * 保存の順番。権限ルールの式評価上限に近い書き込み(申請・手数料の取引)は1回ずつに分け、
 * 途中で失敗したら、それまでの書き込みを元に戻して例外を投げる(中途半端な状態を残さない)。
 *  0. 読むだけの確認: 開いた後にほかの人が更新していないか / 手数料の取引・資金繰りの出金があるか
 *  1. 申請を更新する(変わった項目だけ)
 *  2. 振込手数料の別取引を作る・直す(支払い後に手数料・支払日・口座・会社・事業部・拠点を変えたとき)
 *  3. 資金繰り(CF)の出金を直す・手数料の出金を作る/消す・手数料の取引を消す(まとめて1回で書く)
 *  4. 修正履歴を残す(失敗しても修正は保存済み。呼び出し側で知らせる)
 *  5. 支払い前に支払い者・支払い方法・事業部を変えたときは、新しい担当者へ依頼を出し直す
 */
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type DocumentReference,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { toMonth } from '@core/lib/date';
import { newCashEntryId } from '@core/cash/cashRepo';
import { dedupHash } from '@core/cash/matching';
import type { Transaction } from '@core/lib/types';
import { writeEditHistory, type EditChange } from './editHistory';
import { FlowStaleError, runFollowUps, type FlowResult } from './flowResult';
import { markActionDone, notifyUsers } from './notificationsRepo';
import { notifyPayerAfterApproval, usersForNotification } from './applicationRepo';

export type ApproverEditResult = FlowResult & { historySaved: boolean };

function millis(ts: unknown): number {
  const t = ts as { toMillis?: () => number } | null | undefined;
  return t?.toMillis ? t.toMillis() : 0;
}

/** 空欄・null・未設定は同じ「値なし」として比べる */
function same(a: unknown, b: unknown): boolean {
  const n = (v: unknown) => (v === undefined || v === null || v === '' ? null : v);
  return n(a) === n(b);
}

function isPermissionDenied(e: unknown): boolean {
  return (e as { code?: string } | null)?.code === 'permission-denied';
}

export async function saveApproverEdit(params: {
  transaction: Transaction;
  /** 変わった項目だけの値。posting_month などの導出項目・添付の追加も含めて呼び出し側で作る */
  patch: Record<string, unknown>;
  changes: EditChange[];
  userId: string;
  /** 振込手数料を新しく計上するときの科目(支払手数料)のID */
  feeAccountId: string | null;
}): Promise<ApproverEditResult> {
  const { transaction: t, patch, changes, userId, feeAccountId } = params;
  const db = getDb();
  const txRef = doc(db, 'transactions', t.id);
  const next = { ...t, ...patch } as Transaction;
  const changed = (k: keyof Transaction) => k in patch && !same(t[k], next[k]);
  const label = t.code ?? 'この申請';

  // --- 0. 読むだけの確認 -------------------------------------------------------
  const latest = await getDoc(txRef);
  if (!latest.exists()) throw new FlowStaleError(`${label} は削除されています。一覧を更新して確認してください`);
  const latestData = latest.data() as Transaction;
  if (latestData.status !== t.status || millis(latestData.updated_at) !== millis(t.updated_at)) {
    throw new FlowStaleError(
      `${label} は、開いた後にほかの人が更新しています。いったん閉じて開き直し、最新の内容で修正してください`,
    );
  }

  const paid = !!t.paid_at;
  const feeTouched = changed('transfer_fee');
  const newFee = (next.transfer_fee ?? null) as number | null;
  const wantFee = paid && (newFee ?? 0) > 0;
  const feeSync =
    paid &&
    (feeTouched ||
      changed('payment_date') ||
      changed('payment_account_id') ||
      changed('company_id') ||
      changed('department_id') ||
      changed('hierarchy_node_id'));

  let feeTx: Transaction | null = null;
  if (feeSync) {
    const snap = await getDocs(query(collection(db, 'transactions'), where('fee_of', '==', t.id), limit(5)));
    feeTx =
      snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as Transaction).find((x) => x.source === 'transfer_fee') ??
      null;
  }

  // 手数料の取引をどうするか
  let feeCreate: { ref: DocumentReference; data: Record<string, unknown>; cashEntryId: string | null } | null = null;
  let feeUpdate: { ref: DocumentReference; patch: Record<string, unknown>; revert: Record<string, unknown> } | null = null;
  let feeDelete: Transaction | null = null;
  if (feeTx) {
    if (feeTouched && !wantFee) {
      feeDelete = feeTx;
    } else {
      const p: Record<string, unknown> = {};
      if (feeTouched && newFee != null && newFee !== feeTx.amount) {
        p.amount = newFee;
        p.applied_amount = newFee;
      }
      if (changed('payment_date')) {
        p.posting_date = next.payment_date;
        p.posting_month = toMonth(next.payment_date);
        p.payment_date = next.payment_date;
        p.payment_month = toMonth(next.payment_date);
      }
      if (changed('company_id')) p.company_id = next.company_id ?? 'sum';
      if (changed('department_id')) p.department_id = next.department_id;
      if (changed('hierarchy_node_id')) p.hierarchy_node_id = next.hierarchy_node_id ?? null;
      if (changed('payment_account_id')) p.payment_account_id = next.payment_account_id ?? null;
      if (Object.keys(p).length > 0) {
        const revert: Record<string, unknown> = {};
        for (const k of Object.keys(p)) revert[k] = (feeTx as unknown as Record<string, unknown>)[k] ?? null;
        feeUpdate = { ref: doc(db, 'transactions', feeTx.id), patch: p, revert };
      }
    }
  } else if (feeTouched && wantFee) {
    if (!feeAccountId) {
      throw new Error('科目「支払手数料」が見つからないため、振込手数料を計上できません(マスタ管理で有効にしてください)');
    }
    const ref = doc(collection(db, 'transactions'));
    const cashEntryId = next.payment_account_id ? newCashEntryId() : null;
    feeCreate = {
      ref,
      cashEntryId,
      // 支払い時に作る手数料の取引(flowActions.pay)と同じ項目。作成者・支払った人は修正した承認者
      data: {
        type: 'expense',
        doc_type: null,
        status: 'paid',
        source: 'transfer_fee',
        fee_of: t.id,
        code: `${t.code ?? ''}-手数料`,
        company_id: next.company_id ?? 'sum',
        department_id: next.department_id,
        hierarchy_node_id: next.hierarchy_node_id ?? null,
        account_id: feeAccountId,
        sub_account_id: '',
        fiscal_year: next.fiscal_year ?? null,
        amount: newFee,
        applied_amount: newFee,
        tax_mode: 'inclusive',
        tax_amount: null,
        posting_date: next.payment_date,
        posting_month: toMonth(next.payment_date),
        payment_date: next.payment_date,
        payment_month: toMonth(next.payment_date),
        due_date: null,
        payment_method: 'bank_transfer',
        payment_account_id: next.payment_account_id ?? null,
        payer: 'accounting',
        counterparty: null,
        vendor_id: null,
        description: `振込手数料(${t.code ?? ''})`,
        note: null,
        attachments: [],
        receipts: [],
        approvals: [],
        approval_snapshot: [],
        approval_rule_id: '',
        current_step: 0,
        is_internal: false,
        counterparty_department_id: null,
        evidence_type: null,
        invoice_no: null,
        applied_at: null,
        applied_by: null,
        manager_checked_at: null,
        manager_checked_by: null,
        approved_at: null,
        approved_by: null,
        paid_at: serverTimestamp(),
        paid_by: userId,
        completed_at: null,
        completed_by: null,
        submitted_at: null,
        submitted_by: null,
        confirmed_at: null,
        confirmed_by: null,
        rejected_at: null,
        rejected_by: null,
        reject_reason: null,
        rejected_from_status: null,
        cash_entry_id: cashEntryId,
        recurring_cost_id: null,
        created_by: userId,
        created_at: serverTimestamp(),
        updated_by: userId,
        updated_at: serverTimestamp(),
      },
    };
  }

  // 資金繰り(CF)の出金の書き換え。存在しない出金(手で消された等)は飛ばす
  const entryUpdates: Array<{ id: string; patch: Record<string, unknown> }> = [];
  if (t.cash_entry_id) {
    const p: Record<string, unknown> = {};
    if (changed('amount')) p.amount = next.amount;
    if (changed('payment_date')) p.date = next.payment_date;
    if (changed('payment_account_id') && next.payment_account_id) p.cash_account_id = next.payment_account_id;
    if (Object.keys(p).length > 0) entryUpdates.push({ id: t.cash_entry_id, patch: p });
  }
  if (t.reimbursement_cash_entry_id && changed('amount')) {
    entryUpdates.push({ id: t.reimbursement_cash_entry_id, patch: { amount: next.amount } });
  }
  if (t.prepaid_cash_entry_id && changed('amount')) {
    entryUpdates.push({ id: t.prepaid_cash_entry_id, patch: { amount: next.amount } });
  }
  if (feeTx?.cash_entry_id && feeUpdate) {
    const p: Record<string, unknown> = {};
    if ('amount' in feeUpdate.patch) p.amount = feeUpdate.patch.amount;
    if (changed('payment_date')) p.date = next.payment_date;
    if (changed('payment_account_id') && next.payment_account_id) p.cash_account_id = next.payment_account_id;
    if (Object.keys(p).length > 0) entryUpdates.push({ id: feeTx.cash_entry_id, patch: p });
  }
  const entryDeletes: string[] = feeDelete?.cash_entry_id ? [feeDelete.cash_entry_id] : [];
  const existing = new Set<string>();
  await Promise.all(
    [...entryUpdates.map((u) => u.id), ...entryDeletes].map(async (id) => {
      const s = await getDoc(doc(db, 'cash_entries', id));
      if (s.exists()) existing.add(id);
    }),
  );

  // --- 1. 申請を更新する ----------------------------------------------------------
  try {
    await updateDoc(txRef, { ...patch, updated_at: serverTimestamp(), updated_by: userId });
  } catch (e) {
    if (isPermissionDenied(e)) {
      throw new Error('修正を保存できませんでした(権限がないか、金額・日付が条件を満たしていません)。画面を更新してから、もう一度お試しください');
    }
    throw e;
  }
  const revertMain = async () => {
    const back: Record<string, unknown> = { updated_at: serverTimestamp(), updated_by: userId };
    for (const k of Object.keys(patch)) back[k] = (t as unknown as Record<string, unknown>)[k] ?? null;
    await updateDoc(txRef, back);
  };

  /** 2・3 の失敗時: 書いた分を元に戻して例外にする */
  const rollback = async (what: string, cause: unknown, undo: Array<() => Promise<unknown>>): Promise<never> => {
    console.error(`${what}に失敗しました`, cause);
    const failed: string[] = [];
    for (const u of [...undo, revertMain]) {
      try {
        await u();
      } catch (e) {
        console.error('元に戻す処理に失敗しました', e);
        failed.push('x');
      }
    }
    throw new Error(
      failed.length === 0
        ? `${what}に失敗したため、修正を取り消しました。通信状態を確認して、もう一度お試しください`
        : `${what}に失敗し、修正の一部を元に戻せませんでした。${label} の内容と資金繰りを確認し、管理者に連絡してください`,
    );
  };

  // --- 2. 振込手数料の取引 --------------------------------------------------------
  const undoFee: Array<() => Promise<unknown>> = [];
  if (feeCreate) {
    try {
      await setDoc(feeCreate.ref, feeCreate.data);
      const created = feeCreate.ref;
      undoFee.push(() => deleteDoc(created));
    } catch (e) {
      await rollback('振込手数料の計上', e, []);
    }
  } else if (feeUpdate) {
    try {
      await updateDoc(feeUpdate.ref, { ...feeUpdate.patch, updated_at: serverTimestamp(), updated_by: userId });
      const { ref, revert } = feeUpdate;
      undoFee.push(() => updateDoc(ref, { ...revert, updated_at: serverTimestamp(), updated_by: userId }));
    } catch (e) {
      await rollback('振込手数料の取引の修正', e, []);
    }
  }

  // --- 3. 資金繰り(CF)と手数料の取引の削除(まとめて1回) ----------------------------------
  const batch = writeBatch(db);
  let batchOps = 0;
  for (const u of entryUpdates) {
    if (!existing.has(u.id)) continue;
    batch.update(doc(db, 'cash_entries', u.id), { ...u.patch, updated_at: serverTimestamp() });
    batchOps++;
  }
  if (feeCreate?.cashEntryId && next.payment_account_id) {
    batch.set(doc(db, 'cash_entries', feeCreate.cashEntryId), {
      cash_account_id: next.payment_account_id,
      date: next.payment_date,
      direction: 'out',
      amount: newFee,
      status: 'settled',
      description: `振込手数料 ${t.code ?? ''}`,
      counterparty: null,
      transaction_id: t.id,
      import_batch_id: null,
      dedup_hash: dedupHash({
        cashAccountId: next.payment_account_id,
        date: next.payment_date,
        direction: 'out',
        amount: newFee ?? 0,
        description: `振込手数料 ${t.code ?? ''}`,
      }),
      matched_at: null,
      matched_by: null,
      created_by: userId,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });
    batchOps++;
  }
  for (const id of entryDeletes) {
    if (!existing.has(id)) continue;
    batch.delete(doc(db, 'cash_entries', id));
    batchOps++;
  }
  if (feeDelete) {
    batch.delete(doc(db, 'transactions', feeDelete.id));
    batchOps++;
  }
  if (batchOps > 0) {
    try {
      await batch.commit();
    } catch (e) {
      await rollback('資金繰り(CF)の更新', e, undoFee);
    }
  }

  // --- 4. 修正履歴 ---------------------------------------------------------------
  const historySaved = await writeEditHistory({ transactionId: t.id, changes, userId, action: 'approver-edit' });

  // --- 5. 担当者への依頼の出し直し(支払い前に、担当が変わる項目を直したとき) ------------------
  const steps: Array<[string, () => Promise<unknown>]> = [];
  let noManagerWarning: string | null = null;
  if (t.doc_type && t.status === 'approved' && (changed('payer') || changed('payment_method') || changed('department_id'))) {
    steps.push(['以前の担当者への依頼の取り下げ', () => markActionDone(t.id)]);
    steps.push([
      '支払いの担当者への依頼',
      () =>
        notifyPayerAfterApproval({
          id: t.id,
          code: t.code,
          departmentId: next.department_id,
          createdBy: t.created_by,
          payer: next.payer,
          advance: next.advance === true,
          paymentMethod: next.payment_method,
        }),
    ]);
  } else if (t.doc_type && t.status === 'applied' && changed('department_id')) {
    const buchos = await usersForNotification('bucho', next.department_id).catch(() => null);
    if (buchos && buchos.length === 0) {
      noManagerWarning =
        '新しい事業部には部門長がいないため、部門長確認をする人がいません。必要なら差戻しして、申請者に再申請してもらってください。';
    } else {
      steps.push(['以前の部門長への依頼の取り下げ', () => markActionDone(t.id)]);
      steps.push([
        '新しい事業部の部門長への依頼',
        async () =>
          notifyUsers({
            userIds: buchos ?? (await usersForNotification('bucho', next.department_id)),
            transactionId: t.id,
            message: `${t.code} の部門長確認をお願いします`,
            kind: 'action',
            stage: 'approve',
          }),
      ]);
    }
  }
  const followUp = await runFollowUps(steps);
  const warning = [followUp.warning, noManagerWarning].filter((w): w is string => !!w).join('\n') || null;
  return { warning, historySaved };
}
